import * as React from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '@/components/ui/command';

/**
 * Combobox — dropdown pesquisável (digita e filtra na hora).
 * options: [{ value, label, ...meta }]
 */
export function Combobox({
  value,
  onValueChange,
  options = [],
  placeholder = 'Selecione...',
  emptyText = 'Nenhum resultado encontrado.',
  searchPlaceholder = 'Buscar...',
  className,
  contentClassName,
  renderLabel,
  'data-testid': dataTestid,
}) {
  const [open, setOpen] = React.useState(false);
  const selected = options.find((o) => o.value === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          role="combobox"
          aria-expanded={open}
          data-testid={dataTestid}
          className={cn(
            'w-full justify-between font-normal border-[#27272A] bg-[#141414] text-white hover:bg-[#1A1A1A] hover:text-white',
            className
          )}
        >
          {selected ? (
            renderLabel ? (
              renderLabel(selected)
            ) : (
              selected.label
            )
          ) : (
            <span className="text-[#71717A]">{placeholder}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn(
          'p-0 w-[--radix-popover-trigger-width] bg-[#1A1A1A] border-[#27272A] text-white',
          contentClassName
        )}
        align="start"
      >
        <Command className="bg-transparent">
          <CommandInput
            placeholder={searchPlaceholder}
            className="border-b border-[#27272A] text-white placeholder:text-[#71717A]"
          />
          <CommandList className="max-h-[280px]">
            <CommandEmpty className="py-6 text-center text-sm text-[#71717A]">
              {emptyText}
            </CommandEmpty>
            <CommandGroup>
              {options.map((o) => (
                <CommandItem
                  key={o.value}
                  value={o.label}
                  onSelect={() => {
                    onValueChange(o.value);
                    setOpen(false);
                  }}
                  className="text-white hover:bg-[#27272A] aria-selected:bg-[#27272A] cursor-pointer"
                >
                  <span className="flex items-center gap-2 min-w-0 flex-1">
                    {renderLabel ? renderLabel(o) : o.label}
                  </span>
                  <Check
                    className={cn(
                      'ml-auto h-4 w-4 shrink-0',
                      value === o.value ? 'opacity-100 text-[#D4AF37]' : 'opacity-0'
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
